import { ChartResult, ChartResultCell, dataNotes } from '@employ-engage/data';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataNotes } from '../info-note/data-notes';

export type PivotDisplay = 'count' | 'percent' | 'both';
export type PivotPercentBase = 'respondents' | 'row';

export interface PivotTableProps {
  result?: ChartResult | null;
  display?: PivotDisplay;
  /**
   * What a percentage divides by. Both are counts of *people*: with a
   * multi-select one person occupies several cells, so dividing by the number
   * of answers would make the figure mean nothing.
   */
  percentBase?: PivotPercentBase;
  /** Shade cells by magnitude, turning the table into a heatmap. */
  heatmap?: boolean;
  emptyMessage?: string;
  className?: string;
}

const SUPPRESSED = '—';

/**
 * A cross-tab as a table.
 *
 * Deliberately not built on the shared Table component: that one needs a fixed
 * header list and a per-row id, whereas a pivot derives its columns from the
 * data at render time, and it brings card chrome, a search box and a filter
 * bar that a pivot inside a dashboard tile wants none of.
 *
 * The grand total is the number of respondents, NOT the sum of the cells. With
 * a multi-select those differ, and a totals row that does not add up is the
 * classic pivot bug report - hence the footnote next to it rather than a
 * number that looks wrong.
 */
export function PivotTable({
  result,
  display = 'count',
  percentBase = 'respondents',
  heatmap = false,
  emptyMessage,
  className,
}: PivotTableProps) {
  const { t } = useTranslation(['common']);

  const byPosition = useMemo(() => {
    const map = new Map<string, ChartResultCell>();
    (result?.cells ?? []).forEach((cell) =>
      map.set(`${cell.rowKey[0]}\u0000${cell.colKey[0]}`, cell)
    );
    return map;
  }, [result]);

  const maxN = useMemo(
    () => Math.max(1, ...(result?.cells ?? []).map((c) => c.n ?? 0)),
    [result]
  );

  // Above the early returns: a table that starts empty and then has data must
  // not change how many hooks it runs.
  const notes = useMemo(() => dataNotes(result), [result]);

  if (result?.meta?.suppressedAll) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-center text-sm text-light-secondaryText dark:text-dark-secondaryText">
        <span>
          <i className="fa-solid fa-lock mr-2" aria-hidden />
          {t(
            'Not enough responses yet to show this safely. At least {{count}} are needed.',
            { count: result.meta.minCellCount }
          )}
        </span>
      </div>
    );
  }

  if (!result?.rowKeys?.length) {
    return (
      <div className="p-4 text-center text-sm text-light-secondaryText dark:text-dark-secondaryText">
        {emptyMessage ?? t('No responses in this range yet')}
      </div>
    );
  }

  /**
   * A row total across one column is that column.
   *
   * With no breakdown the engine still emits a single synthetic column, and it
   * is already labelled "Total" — so the table was rendering two columns
   * headed TOTAL showing the same number. The separate totals column earns its
   * place only once there is more than one column to add up.
   */
  const showTotalsColumn = result.colKeys.length > 1;

  /** The authoritative respondent base: "applied to" when some said N/A. */
  const base =
    result.totals.grand === null
      ? null
      : result.meta.baseRespondents ?? result.totals.grand;

  const denominatorFor = (rowKey: string) =>
    percentBase === 'row'
      ? result.totals.rows[rowKey] ?? 0
      : // The respondents the question applied to, which is not everyone when
        // an option is flagged not-applicable.
        result.meta.baseRespondents ?? result.meta.respondentsInScope;

  const renderCell = (cell: ChartResultCell | undefined, rowKey: string) => {
    if (!cell || cell.suppressed) {
      return (
        <span
          title={t(
            'Hidden to protect anonymity — fewer than {{count}} responses.',
            {
              count: result.meta.minCellCount,
            }
          )}
        >
          {SUPPRESSED}
        </span>
      );
    }
    const n = cell.n ?? 0;
    const denominator = denominatorFor(rowKey);
    const percent = denominator > 0 ? Math.round((n / denominator) * 100) : 0;

    if (display === 'percent') return <>{percent}%</>;
    if (display === 'both') {
      return (
        <>
          {n}{' '}
          <span className="text-light-secondaryText dark:text-dark-secondaryText">
            ({percent}%)
          </span>
        </>
      );
    }
    return <>{n}</>;
  };

  const shade = (cell: ChartResultCell | undefined) => {
    if (!heatmap || !cell || cell.suppressed) return undefined;
    const intensity = Math.min(1, (cell.n ?? 0) / maxN);
    // A tint of the leading series colour rather than a second hue, so the
    // table reads as one surface.
    return {
      backgroundColor: `rgba(31, 138, 77, ${(intensity * 0.28).toFixed(3)})`,
    };
  };

  const headCell =
    'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-light-secondaryText dark:text-dark-secondaryText';
  const bodyCell = 'px-3 py-2 text-sm tabular-nums';

  return (
    <div className={classNames('w-full overflow-x-auto', className)}>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-light-alternate dark:border-dark-alternate">
            <th
              className={classNames(
                headCell,
                'sticky left-0 z-10 bg-white dark:bg-dark-secondaryBackground'
              )}
              scope="col"
            >
              {t('Answer')}
            </th>
            {result.colLabels.map((label, i) => (
              <th
                key={result.colKeys[i]}
                className={classNames(headCell, 'text-right')}
                scope="col"
              >
                {label[0]}
              </th>
            ))}
            {showTotalsColumn && (
              <th className={classNames(headCell, 'text-right')} scope="col">
                {t('Total')}
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {result.rowKeys.map((rowKey, rowIndex) => {
            const rowTotal = result.totals.rows[rowKey];
            return (
              <tr
                key={rowKey}
                className="border-b border-light-alternate/50 dark:border-dark-alternate/50"
              >
                <th
                  scope="row"
                  className={classNames(
                    bodyCell,
                    'sticky left-0 z-10 bg-white text-left font-medium dark:bg-dark-secondaryBackground'
                  )}
                >
                  {result.rowLabels[rowIndex]?.[0]}
                </th>
                {result.colKeys.map((colKey) => {
                  const cell = byPosition.get(`${rowKey}\u0000${colKey}`);
                  return (
                    <td
                      key={colKey}
                      className={classNames(bodyCell, 'text-right')}
                      style={shade(cell)}
                    >
                      {renderCell(cell, rowKey)}
                    </td>
                  );
                })}
                {showTotalsColumn && (
                  <td
                    className={classNames(bodyCell, 'text-right font-semibold')}
                  >
                    {rowTotal === null ? SUPPRESSED : rowTotal}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>

        <tfoot>
          <tr className="border-t-2 border-light-alternate dark:border-dark-alternate">
            <th
              scope="row"
              className={classNames(
                bodyCell,
                'sticky left-0 z-10 bg-white text-left font-semibold dark:bg-dark-secondaryBackground'
              )}
            >
              {result.meta.notApplicableRespondents > 0
                ? t('Applied to')
                : t('Respondents')}
            </th>
            {result.colKeys.map((colKey) => {
              const total = result.totals.columns[colKey];
              return (
                <td
                  key={colKey}
                  className={classNames(bodyCell, 'text-right font-semibold')}
                >
                  {/* With one column there is no totals column beside it, so
                      this cell carries the respondent base — which is the
                      smaller "applied to" figure when some said N/A, and is
                      what the row label above has switched to describing. */}
                  {showTotalsColumn
                    ? total === null
                      ? SUPPRESSED
                      : total
                    : base === null
                    ? SUPPRESSED
                    : base}
                </td>
              );
            })}
            {showTotalsColumn && (
              <td className={classNames(bodyCell, 'text-right font-semibold')}>
                {base === null ? SUPPRESSED : base}
              </td>
            )}
          </tr>
        </tfoot>
      </table>

      {/* Sits next to the totals on purpose: this is what explains a total
          that is smaller than the cells above it, or missing altogether. */}
      <DataNotes notes={notes} className="mt-2 px-3" maxLines={3} />
    </div>
  );
}

export default PivotTable;
